import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScenarioAssistantModal } from '~/widgets/scenario/components/scenario-assistant-modal/ScenarioAssistantModal';
import { useScenarioAssistant } from '~/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import {
  ScenarioPageModalType,
  useScenarioStore,
} from '~/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import {
  ScenarioConditionsWidget,
  ScenarioConditionsWidgetMode,
} from '~/widgets/scenario/ScenarioConditionsWidget';

import { ClearAllButton } from './header/clear-all-button/ClearAllButton';
import {
  useScenarioEntityTags,
  useScenarioFiltersEntityTags,
} from './hooks/use-scenario-entity-tags';
import * as S from './Scenario.styles';
import { ScenarioAssetSearch } from './scenario-asset-search/ScenarioAssetSearch';

export const Scenario = () => {
  const { t } = useTranslation([
    'widget',
    'chart',
    'scenario',
    'screener',
    'scenarioCommander',
    'countries',
    'analyze',
  ]);
  const [scenarioAssistantConditionId, setScenarioAssistantConditionId] =
    useState<string | undefined>();

  const {
    activeScenarioPageModal,
    scenarioPageEntityTags,
    setActiveScenarioPageModal,
    setScenarioPageEntityTags,
    setScenarioPageEntityTagsLength,
  } = useScenarioStore(state => ({
    activeScenarioPageModal: state.activeScenarioPageModal,
    scenarioPageEntityTags: state.scenarioPageEntityTags,
    setScenarioPageEntityTagsLength: state.setScenarioPageEntityTagsLength,
    setActiveScenarioPageModal: state.setActiveScenarioPageModal,
    setScenarioPageEntityTags: state.setScenarioPageEntityTags,
  }));

  const { data: scenarioEntityTags, isLoading: isScenarioEntityTagsLoading } =
    useScenarioEntityTags();
  const {
    data: scenarioFiltersEntityTags,
    isLoading: isScenarioFiltersEntitiesTagsLoading,
  } = useScenarioFiltersEntityTags();

  useEffect(() => {
    if (!isScenarioEntityTagsLoading && !isScenarioFiltersEntitiesTagsLoading) {
      const intersectionEntitiesTags = [
        ...scenarioFiltersEntityTags,
        scenarioEntityTags,
      ]
        .filter(array => !!array.length)
        .reduce((a, b) => {
          if (a.length && b.length) {
            return a.filter(c => b.includes(c));
          }

          a.push(...b);
          return a;
        }, []);

      setScenarioPageEntityTags({
        tagsAddedViaFilters: intersectionEntitiesTags,
      });
    }
  }, [
    scenarioFiltersEntityTags.length,
    scenarioEntityTags.length,
    isScenarioFiltersEntitiesTagsLoading,
    isScenarioEntityTagsLoading,
  ]);

  useEffect(() => {
    setScenarioPageEntityTagsLength(scenarioEntityTags.length);
  }, [scenarioEntityTags.length]);

  const changeEditFilter = (conditionId: string) => {
    if (conditionId) {
      setScenarioAssistantConditionId(conditionId);
      setActiveScenarioPageModal(ScenarioPageModalType.addCondition);
    }
  };

  const resetAskedQuestions = useScenarioAssistant(
    state => state.resetAskedQuestions
  );

  const displayedEntitiesTags = [
    ...(scenarioPageEntityTags.tagsAddedViaFilters ?? []),
    ...(scenarioPageEntityTags.tagsAddedViaSearch ?? []),
  ].sort((a, b) => a.localeCompare(b));

  return (
    <S.Root>
      <S.HeaderWrapper>
        <S.StyledTitle>{t('scenario:scenario')}</S.StyledTitle>
        <ClearAllButton />
      </S.HeaderWrapper>
      <ScenarioConditionsWidget
        entitiesTags={displayedEntitiesTags}
        mode={ScenarioConditionsWidgetMode.page}
        noAssetsButtonLabel={t('scenario:emptyState.addAssets')}
        onEditAssets={() =>
          setActiveScenarioPageModal(ScenarioPageModalType.addAssets)
        }
        onAddConditions={() => {
          resetAskedQuestions();
          setActiveScenarioPageModal(ScenarioPageModalType.addCondition);
        }}
        onEditAssistantCondition={changeEditFilter}
      />
      <ScenarioAssistantModal
        editFilterKey={scenarioAssistantConditionId}
        isOpen={activeScenarioPageModal === ScenarioPageModalType.addCondition}
        onClose={() => {
          setScenarioAssistantConditionId(undefined);
          setActiveScenarioPageModal();
        }}
        resetEditFilterKey={() => {
          setScenarioAssistantConditionId(undefined);
        }}
      />
      {activeScenarioPageModal === ScenarioPageModalType.addAssets && (
        <ScenarioAssetSearch
          isOpen
          onClose={() => setActiveScenarioPageModal()}
        />
      )}
    </S.Root>
  );
};
